import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import * as api from './duplicatesApi';
import { DuplicatesUnavailableError, duplicatesErrorMessage } from './duplicatesApi';
import { sortCandidates } from './format';
import type {
  DuplicateCandidate,
  DuplicateEntityType,
  DuplicateQuery,
  DuplicateScan,
  MatchHit,
  MatchProbe,
  MergePlan,
  MergePreview,
} from '../../types/duplicates';

export interface LoadState {
  loading: boolean;
  /** One short line, already human-readable */
  error: string | null;
  /** The endpoints do not exist on this server yet */
  unavailable: boolean;
}

const failure = (error: any): Pick<LoadState, 'error' | 'unavailable'> => ({
  error: duplicatesErrorMessage(error),
  unavailable: error instanceof DuplicatesUnavailableError,
});

/* ------------------------------------------------------------------ */
/* The review queue                                                    */
/* ------------------------------------------------------------------ */

/**
 * The review queue.
 *
 * What is being looked for comes from the caller — the page keeps it in the
 * address bar — and the queue holds whatever else the reviewer changes while
 * they are here. The caller's terms win, so a filter arriving from the URL is
 * never masked by an older one this hook is still holding.
 */
export function useDuplicateQueue(asked: DuplicateQuery = {}) {
  // The caller may build its terms afresh on every render, so the queue
  // follows what they say rather than the identity of the object
  const askedKey = JSON.stringify(asked);
  const asking = useMemo<DuplicateQuery>(() => JSON.parse(askedKey) as DuplicateQuery, [askedKey]);
  const [own, setOwn] = useState<DuplicateQuery>({});
  const query = useMemo<DuplicateQuery>(() => ({ ...own, ...asking }), [own, asking]);

  const latest = useRef(query);
  latest.current = query;
  const setQuery = useCallback<Dispatch<SetStateAction<DuplicateQuery>>>(
    (update) => setOwn(typeof update === 'function' ? update(latest.current) : update),
    [],
  );

  const [scan, setScan] = useState<DuplicateScan | null>(null);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });
  const [busyId, setBusyId] = useState<string | null>(null);
  const run = useRef(0);

  const reload = useCallback(async () => {
    const ticket = ++run.current;
    setState((s) => ({ ...s, loading: true }));
    try {
      const result = await api.scanDuplicates(query);
      if (ticket !== run.current) return;
      setScan(result);
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      if (ticket !== run.current) return;
      setState({ loading: false, ...failure(error) });
    }
  }, [query]);

  useEffect(() => {
    reload();
  }, [reload]);

  const candidates = useMemo(() => sortCandidates(scan?.candidates || []), [scan]);

  /** Drops a pair and anything else that involved the record just folded away */
  const forget = useCallback((pairId: string, goneId?: string) => {
    setScan((current) =>
      current
        ? {
            ...current,
            candidates: current.candidates.filter(
              (candidate) =>
                candidate.id !== pairId && (!goneId || (candidate.left.id !== goneId && candidate.right.id !== goneId)),
            ),
          }
        : current,
    );
  }, []);

  const dismiss = useCallback(
    async (candidate: DuplicateCandidate): Promise<string | null> => {
      setBusyId(candidate.id);
      try {
        await api.dismissPair(candidate.id);
        forget(candidate.id);
        return null;
      } catch (error) {
        return duplicatesErrorMessage(error);
      } finally {
        setBusyId(null);
      }
    },
    [forget],
  );

  return {
    query,
    setQuery,
    scan,
    candidates,
    busyId,
    ...state,
    reload,
    dismiss,
    forget,
  };
}

/* ------------------------------------------------------------------ */
/* What a merge would do                                               */
/* ------------------------------------------------------------------ */

/**
 * Keeps a live answer to "what happens if I press merge". The last good
 * preview stays on screen while a new one loads, so the dialog never blanks
 * out under the reviewer's hands.
 */
export function useMergePreview(plan: MergePlan | null, delayMs = 200) {
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [state, setState] = useState<LoadState>({ loading: false, error: null, unavailable: false });
  const run = useRef(0);
  const key = plan ? JSON.stringify(plan) : '';

  useEffect(() => {
    if (!key) {
      setPreview(null);
      setState({ loading: false, error: null, unavailable: false });
      return;
    }
    const ticket = ++run.current;
    setState((s) => ({ ...s, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const result = await api.previewMerge(JSON.parse(key) as MergePlan);
        if (ticket !== run.current) return;
        setPreview(result);
        setState({ loading: false, error: null, unavailable: false });
      } catch (error) {
        if (ticket !== run.current) return;
        setState({ loading: false, ...failure(error) });
      }
    }, delayMs);
    return () => clearTimeout(timer);
  }, [key, delayMs]);

  return { preview, ...state };
}

/* ------------------------------------------------------------------ */
/* Matching one record before it is created                            */
/* ------------------------------------------------------------------ */

export type ProbeState = 'idle' | 'checking' | 'done' | 'failed';

/**
 * Asks the server whether this record already exists. Nothing here may block
 * the caller: a probe that fails, or one the server cannot answer, simply
 * reports no matches.
 */
export function useMatchProbe() {
  const [hits, setHits] = useState<MatchHit[]>([]);
  const [status, setStatus] = useState<ProbeState>('idle');
  const run = useRef(0);

  const probe = useCallback(async (input: MatchProbe, timeoutMs?: number): Promise<MatchHit[]> => {
    const ticket = ++run.current;
    setStatus('checking');
    try {
      const answer = await api.probeMatches(input, timeoutMs);
      const found = Array.isArray(answer) ? answer : [];
      if (ticket !== run.current) return [];
      setHits(found);
      setStatus('done');
      return found;
    } catch {
      if (ticket !== run.current) return [];
      setHits([]);
      setStatus('failed');
      return [];
    }
  }, []);

  const reset = useCallback(() => {
    run.current++;
    setHits([]);
    setStatus('idle');
  }, []);

  return { hits, status, probe, reset };
}

/** Only a match worth stopping for; anything weaker must not slow an import down */
export const blockingHits = (hits: MatchHit[]): MatchHit[] =>
  hits.filter((hit) => hit.verdict === 'certain' || hit.verdict === 'likely');

export const strongestVerdict = (hits: MatchHit[]): DuplicateCandidate['verdict'] | null =>
  hits.some((hit) => hit.verdict === 'certain')
    ? 'certain'
    : hits.some((hit) => hit.verdict === 'likely')
      ? 'likely'
      : hits.length
        ? 'possible'
        : null;

export type { DuplicateEntityType };
